import { CircularProgress, DialogActions, DialogContent, TextField } from '@mui/material';
import { Alert, AlertTitle, Autocomplete } from '@mui/material';
import { UserDto } from '@groundfloor-platform/mgmt-service-g-sdk';
import { parseErrorMessage } from '../../../../../core/api/api.context';
import { Modal } from '../../../../../ui/components/pure/modal/modal.component';
import { Snackbar } from '../../../../../ui/components/pure/snackbar/snackbar.component';
import { TextButton } from '../../../../../ui/components/pure/text-button/text-button.component';
import { useAutocompletePopperStyles } from '../../../../../ui/components/pure/autocomplete/autocomplete.styles';
import { ReserveContainerActionProps, useReserveContainerAction } from './reserve-container-action.hook';

export function ReserveContainerActionModal(props: ReserveContainerActionProps) {
  const { container, hub, occupants, selectedUser, error, submitting, handlers } = useReserveContainerAction(props);
  // Raw lab Autocomplete rather than our wrapper, so it needs the popper class applying by hand.
  const popperClasses = useAutocompletePopperStyles();

  if (container.error || hub.error) {
    return (
      <Snackbar severity="error" onClose={() => props.onAbort()}>
        Sorry, there was an error getting the delivery container details. Please try again.
      </Snackbar>
    );
  }

  return (
    <Modal
      title="Reserve delivery container"
      open={true}
      loading={container.isLoading || hub.isLoading || submitting}
      maxWidth="xs"
      onClose={handlers.onCancelIntent}
    >
      <DialogContent style={{ padding: '16px 24px', fontSize: '14px' }}>
        {error ? (
          <Alert variant="outlined" severity="error" style={{ marginBottom: '12px' }}>
            <AlertTitle>Something went wrong</AlertTitle>
            {parseErrorMessage(error.message)}
          </Alert>
        ) : null}
        <p>Search for the occupant to reserve this delivery container for.</p>
        <Autocomplete<UserDto>
          classes={{ popper: popperClasses.popper }}
          options={occupants.data ?? []}
          value={selectedUser}
          loading={occupants.isLoading}
          getOptionLabel={(option) => option.displayName || ''}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          // The results are already filtered server-side, so don't filter again client-side.
          filterOptions={(options) => options}
          noOptionsText="No occupants found"
          onChange={(_event, value) => handlers.onSelectUser(value)}
          onInputChange={(_event, value, reason) => {
            if (reason === 'input') {
              handlers.onSearchChange(value);
            }
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              variant="standard"
              label="Search occupants"
              placeholder="Search by name"
              autoFocus
              slotProps={{
                ...params.slotProps,

                input: {
                  ...params.slotProps.input,
                  endAdornment: (
                    <>
                      {occupants.isLoading ? <CircularProgress size={16} /> : null}
                      {params.slotProps.input.endAdornment}
                    </>
                  )
                }
              }}
            />
          )}
        />
      </DialogContent>
      <DialogActions style={{ borderTop: '1px solid #EBEDF2', padding: '16px 24px' }}>
        <TextButton color="secondary" variant="outlined" onClick={handlers.onCancelIntent}>
          Cancel
        </TextButton>
        <TextButton color="primary" variant="outlined" disabled={!selectedUser} onClick={handlers.onSubmit}>
          Reserve
        </TextButton>
      </DialogActions>
    </Modal>
  );
}
